import { clients } from '@/lib/https';
import type { OrgRole } from '../../org-permissions/types/permissions.types';
import type {
  IamUser,
  IamGetUsersResponse,
  IamGetUserResponse,
  IamMembership,
  OrgMember,
  OrgInvitation,
  GetMembersParams,
  InviteMemberInput,
} from '../types/org-users.types';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toMember(user: IamUser, blocksOrgId: string): OrgMember {
  const membership = user.memberships?.find((m) => m.organizationId === blocksOrgId);
  const role = membership?.roles?.[0] ?? null;

  return {
    id: user.itemId,
    email: user.email,
    firstName: user.firstName || null,
    lastName: user.lastName || null,
    role: role as OrgRole,
    imageUrl: user.profileImageUrl || null,
    createdAt: new Date(user.createdDate).getTime(),
    active: user.active,
  };
}

function toInvitation(user: IamUser, blocksOrgId: string): OrgInvitation {
  const membership = user.memberships?.find((m) => m.organizationId === blocksOrgId);
  const role = membership?.roles?.[0] ?? null;

  return {
    id: user.itemId,
    email: user.email,
    role: role as OrgRole,
    status: 'pending',
    createdAt: new Date(user.createdDate).getTime(),
  };
}

async function fetchBlocksUser(userId: string): Promise<IamUser | null> {
  try {
    const result = await clients.get<IamGetUserResponse>(
      `/idp/v1/Iam/GetUser?id=${encodeURIComponent(userId)}&ProjectKey=${encodeURIComponent(projectKey)}`
    );
    return result.data || null;
  } catch {
    return null;
  }
}

async function findBlocksUserByEmail(email: string): Promise<IamUser | null> {
  try {
    const result = await clients.post<IamGetUsersResponse>(
      '/idp/v1/Iam/GetUsers',
      JSON.stringify({
        page: 0,
        pageSize: 1,
        sort: { property: 'createdDate', isDescending: true },
        filter: { email, name: '' },
        projectKey,
      })
    );
    return result.data?.[0] || null;
  } catch {
    return null;
  }
}

async function saveRolesAndPermissions(
  userId: string,
  memberships: IamMembership[]
): Promise<void> {
  await clients.post<any>(
    '/idp/v1/Iam/SaveRolesAndPermissions',
    JSON.stringify({
      userId,
      memberships,
      projectKey,
    })
  );
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export async function getOrgMembers(
  blocksOrgId: string,
  params: GetMembersParams = {}
): Promise<{ members: OrgMember[]; totalCount: number }> {
  try {
    const response = await clients.post<IamGetUsersResponse>(
      '/idp/v1/Iam/GetUsers',
      JSON.stringify({
        page: params.page ?? 0,
        pageSize: params.pageSize ?? 50,
        sort: { property: 'createdDate', isDescending: true },
        status: { active: true },
        filter: {
          organizationId: blocksOrgId,
          email: params.email || '',
          name: params.name || '',
        },
        projectKey,
      })
    );

    const members = (response.data || [])
      .map((u) => toMember(u, blocksOrgId))
      .filter((m) => m.active);

    return { members, totalCount: response.totalCount };
  } catch (error) {
    console.error('Error fetching org members:', error);
    return { members: [], totalCount: 0 };
  }
}

export async function updateMemberRole(
  blocksOrgId: string,
  userId: string,
  role: OrgRole
): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(userId);
  if (!user) throw new Error('Member not found');

  const currentMembership = user.memberships?.find((m) => m.organizationId === blocksOrgId);
  if (!currentMembership) throw new Error('Member is not part of this organization');

  // Update only this org's role, keep all other memberships intact
  const updatedMemberships: IamMembership[] = user.memberships.map((m) => {
    if (m.organizationId === blocksOrgId) {
      return {
        organizationId: m.organizationId,
        roles: [role],
        permissions: m.permissions || [],
      };
    }
    return {
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    };
  });

  await saveRolesAndPermissions(userId, updatedMemberships);
  return { success: true };
}

export async function removeMember(
  blocksOrgId: string,
  userId: string
): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(userId);
  if (!user) throw new Error('Member not found');

  // Remove this org's membership, keep all others
  const remainingMemberships: IamMembership[] = user.memberships
    .filter((m) => m.organizationId !== blocksOrgId)
    .map((m) => ({
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    }));

  // If no memberships remain, add the default org
  if (remainingMemberships.length === 0) {
    remainingMemberships.push({
      organizationId: 'default',
      roles: ['user'],
      permissions: [],
    });
  }

  await saveRolesAndPermissions(userId, remainingMemberships);
  return { success: true };
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export async function getOrgInvitations(
  blocksOrgId: string,
  page = 0,
  pageSize = 50
): Promise<{ invitations: OrgInvitation[]; totalCount: number }> {
  try {
    const response = await clients.post<IamGetUsersResponse>(
      '/idp/v1/Iam/GetUsers',
      JSON.stringify({
        page,
        pageSize,
        sort: { property: 'createdDate', isDescending: true },
        status: { active: false },
        filter: {
          organizationId: blocksOrgId,
          email: '',
          name: '',
        },
        projectKey,
      })
    );

    const invitations = (response.data || [])
      .filter((u) => !u.active)
      .map((u) => toInvitation(u, blocksOrgId));

    return { invitations, totalCount: response.totalCount };
  } catch (error) {
    console.error('Error fetching org invitations:', error);
    return { invitations: [], totalCount: 0 };
  }
}

export interface InviteMemberResult {
  success: boolean;
  code?: 'ALREADY_MEMBER' | 'DUPLICATE_INVITATION';
  existingInvitation?: {
    id: string;
    email: string;
    role: OrgRole | null;
    status: string;
    createdAt: number;
  };
  invitation?: OrgInvitation;
}

export async function inviteMember(
  blocksOrgId: string,
  data: InviteMemberInput
): Promise<InviteMemberResult> {
  // Check if user already exists in Blocks
  const existingUser = await findBlocksUserByEmail(data.email);

  const memberships: IamMembership[] = [
    {
      organizationId: 'default',
      roles: ['user'],
      permissions: [],
    },
    {
      organizationId: blocksOrgId,
      roles: [data.role],
      permissions: [],
    },
  ];

  if (existingUser) {
    const existingMembership = existingUser.memberships?.find(
      (m) => m.organizationId === blocksOrgId
    );

    if (existingMembership) {
      if (existingUser.active) {
        return {
          success: false,
          code: 'ALREADY_MEMBER',
        };
      }

      // Inactive user with existing membership = pending invitation
      const role = existingMembership.roles?.[0] ?? null;
      return {
        success: false,
        code: 'DUPLICATE_INVITATION',
        existingInvitation: {
          id: existingUser.itemId,
          email: existingUser.email,
          role: role as OrgRole,
          status: 'pending',
          createdAt: new Date(existingUser.createdDate).getTime(),
        },
      };
    }

    // User exists but NOT in this org — just add the org membership via SaveRolesAndPermissions
    const updatedMemberships: IamMembership[] = [
      ...(existingUser.memberships || []).map((m) => ({
        organizationId: m.organizationId,
        roles: m.roles,
        permissions: m.permissions || [],
      })),
      {
        organizationId: blocksOrgId,
        roles: [data.role],
        permissions: [],
      },
    ];

    await saveRolesAndPermissions(existingUser.itemId, updatedMemberships);

    return {
      success: true,
      invitation: {
        id: existingUser.itemId,
        email: data.email,
        role: data.role as OrgRole,
        status: 'pending',
        createdAt: Date.now(),
      },
    };
  }

  // Brand new user — create via Iam/Create
  const result = await clients.post<any>(
    '/idp/v1/Iam/Create',
    JSON.stringify({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      userPassType: 1,
      userCreationType: 1,
      platform: 'legal_template',
      projectKey,
      organizationId: blocksOrgId,
      memberships,
    })
  );

  return {
    success: true,
    invitation: {
      id: result.itemId || result.id,
      email: data.email,
      role: data.role as OrgRole,
      status: 'pending',
      createdAt: Date.now(),
    },
  };
}

export async function revokeInvitation(
  blocksOrgId: string,
  invitationId: string
): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(invitationId);
  if (!user) throw new Error('Invitation not found');

  // Remove this org's membership, keep all others
  const remainingMemberships: IamMembership[] = (user.memberships || [])
    .filter((m) => m.organizationId !== blocksOrgId)
    .map((m) => ({
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    }));

  if (remainingMemberships.length === 0) {
    remainingMemberships.push({
      organizationId: 'default',
      roles: ['user'],
      permissions: [],
    });
  }

  await saveRolesAndPermissions(invitationId, remainingMemberships);
  return { success: true };
}

export async function resendInvitation(
  blocksOrgId: string,
  invitationId: string
): Promise<{ success: boolean }> {
  await clients.post<any>(
    '/idp/v1/Iam/ResendActivation',
    JSON.stringify({
      userId: invitationId,
      mailPurpose: 'Activation',
      projectKey,
    })
  );
  return { success: true };
}

export async function updateInvitationRoleAndResend(
  blocksOrgId: string,
  invitationId: string,
  newRole: OrgRole
): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(invitationId);
  if (!user) throw new Error('Invitation not found');

  // Update the role for this org's membership
  const updatedMemberships: IamMembership[] = user.memberships.map((m) => {
    if (m.organizationId === blocksOrgId) {
      return {
        organizationId: m.organizationId,
        roles: [newRole],
        permissions: m.permissions || [],
      };
    }
    return {
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    };
  });

  await saveRolesAndPermissions(invitationId, updatedMemberships);

  // Resend activation
  await clients.post<any>(
    '/idp/v1/Iam/ResendActivation',
    JSON.stringify({
      userId: invitationId,
      mailPurpose: 'Activation',
      projectKey,
    })
  );

  return { success: true };
}
